import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useI18n } from '../../i18n/I18nContext';
import api from '../../utils/api';

const TIER_COLORS = {
  basic: '#6c757d',    // gray
  middle: '#0d6efd',   // blue
  premium: '#ffc107'   // gold
};

const TIER_LABELS = {
  basic: 'Basic',
  middle: 'Middle',
  premium: 'Premium'
};

function AdminOwners() {
  const { user } = useAuth();
  const { t } = useI18n();

  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ email: '', password: '', name: '', phone: '', whatsapp_number: '', line_id: '', extra_notes: '', active: true });

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterTier, setFilterTier] = useState('all');
  const [sortBy, setSortBy] = useState('name');

  useEffect(() => {
    if (user) loadData();
  }, [user]);

  async function loadData() {
    try {
      const data = await api.get('/admin/owners');
      setOwners(data);
    } catch (err) {
      console.error('Failed to load:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/admin/owners/\${editingId}`, form);
      } else {
        await api.post('/admin/owners', form);
      }
      setShowForm(false);
      setEditingId(null);
      setForm({ email: '', password: '', name: '', phone: '', whatsapp_number: '', line_id: '', extra_notes: '', active: true });
      loadData();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  }

  async function handleTierChange(ownerId, newTier) {
    try {
      await api.put(`/admin/owners/\${ownerId}/tier`, { tier: newTier });
      // Update local state
      setOwners(prev => prev.map(o => o.id === ownerId ? { ...o, tier: newTier } : o));
    } catch (err) {
      alert('Error updating tier: ' + err.message);
    }
  }

  function handleEdit(owner) {
    setForm({
      email: owner.email,
      password: '',
      name: owner.name,
      phone: owner.phone || '',
      whatsapp_number: owner.whatsapp_number || '',
      line_id: owner.line_id || '',
      extra_notes: owner.extra_notes || '',
      active: owner.user_active
    });
    setEditingId(owner.id);
    setShowForm(true);
  }

  // Filter and sort owners
  const filteredOwners = owners
    .filter(o => {
      const matchesSearch = !searchTerm ||
        o.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        o.email.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = filterStatus === 'all' ||
        (filterStatus === 'active' && o.user_active) ||
        (filterStatus === 'inactive' && !o.user_active);
      const matchesTier = filterTier === 'all' || (o.tier || 'basic') === filterTier;
      return matchesSearch && matchesStatus && matchesTier;
    })
    .sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'email') return a.email.localeCompare(b.email);
      if (sortBy === 'properties') return (b.property_count || 0) - (a.property_count || 0);
      if (sortBy === 'tier') {
        const tierOrder = { premium: 0, middle: 1, basic: 2 };
        return tierOrder[a.tier || 'basic'] - tierOrder[b.tier || 'basic'];
      }
      return 0;
    });

  if (loading) {
    return <div className="flex justify-center" style={{ padding: '40px 0' }}><div className="spinner"></div></div>;
  }

  return (
    <div>
      {/* Page Header */}
      <div className="flex justify-between items-center mb-4">
        <h1 style={{ fontSize: '20px', fontWeight: '600' }}>{t('admin.owners')}</h1>
        <button onClick={() => { setShowForm(true); setEditingId(null); setForm({ email: '', password: '', name: '', phone: '', whatsapp_number: '', line_id: '', extra_notes: '', active: true }); }} className="btn btn-primary" style={{ padding: '8px 16px', fontSize: '14px' }}>
          + {t('common.new')}
        </button>
      </div>
        {/* Filters */}
        <div className="card mb-4">
          <div className="flex gap-2 flex-wrap items-end">
            <div style={{ flex: '2', minWidth: '200px' }}>
              <label className="label text-xs">{t('common.search')}</label>
              <input
                type="text"
                className="input"
                placeholder={t('admin.searchByNameEmail')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div style={{ flex: '1', minWidth: '120px' }}>
              <label className="label text-xs">{t('common.status')}</label>
              <select className="input" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="all">{t('common.all')}</option>
                <option value="active">{t('common.active')}</option>
                <option value="inactive">{t('common.inactive')}</option>
              </select>
            </div>
            <div style={{ flex: '1', minWidth: '120px' }}>
              <label className="label text-xs">Tier</label>
              <select className="input" value={filterTier} onChange={(e) => setFilterTier(e.target.value)}>
                <option value="all">{t('common.all')}</option>
                <option value="basic">Basic</option>
                <option value="middle">Middle</option>
                <option value="premium">Premium</option>
              </select>
            </div>
            <div style={{ flex: '1', minWidth: '120px' }}>
              <label className="label text-xs">{t('common.sortBy')}</label>
              <select className="input" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="name">{t('admin.nameAZ')}</option>
                <option value="email">{t('common.email')}</option>
                <option value="properties">{t('admin.mostProperties')}</option>
                <option value="tier">Tier</option>
              </select>
            </div>
          </div>
          <div className="text-secondary text-xs mt-2">
            {t('common.showing', { count: filteredOwners.length, total: owners.length })} {t('admin.owners').toLowerCase()}
          </div>
        </div>

        {showForm && (
          <div className="card mb-4">
            <h3 style={{ fontSize: '16px', marginBottom: '16px' }}>{editingId ? t('admin.editOwner') : t('admin.newOwner')}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="label">{t('admin.loginEmail')} *</label>
                <input type="email" className="input" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} required disabled={!!editingId} />
              </div>
              {!editingId && (
                <div className="form-group">
                  <label className="label">{t('common.password')} *</label>
                  <input type="password" className="input" value={form.password} onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))} required />
                </div>
              )}
              <div className="form-group">
                <label className="label">{t('common.name')} *</label>
                <input type="text" className="input" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label className="label">{t('common.phone')}</label>
                <input type="text" className="input" value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="label">{t('admin.whatsappNumber')}</label>
                <input type="text" className="input" value={form.whatsapp_number} onChange={(e) => setForm(f => ({ ...f, whatsapp_number: e.target.value }))} placeholder="+66812345678" />
              </div>
              <div className="form-group">
                <label className="label">{t('admin.lineId')}</label>
                <input type="text" className="input" value={form.line_id} onChange={(e) => setForm(f => ({ ...f, line_id: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="label">{t('admin.notes')}</label>
                <textarea className="input" rows={2} value={form.extra_notes} onChange={(e) => setForm(f => ({ ...f, extra_notes: e.target.value }))} />
              </div>
              <div className="form-group">
                <label><input type="checkbox" checked={form.active} onChange={(e) => setForm(f => ({ ...f, active: e.target.checked }))} /> {t('common.active')}</label>
              </div>
              <div className="flex gap-2">
                <button type="submit" className="btn btn-primary">{t('common.save')}</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>{t('common.cancel')}</button>
              </div>
            </form>
          </div>
        )}

        {filteredOwners.length === 0 ? (
          <div className="card text-center text-secondary">
            {t('admin.noOwnersFound')}
          </div>
        ) : (
          filteredOwners.map(o => (
            <div key={o.id} className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <div className="flex items-center gap-2">
                  <h3 style={{ fontSize: '14px' }}>{o.name}</h3>
                  <span 
                    className="badge" 
                    style={{ 
                      background: TIER_COLORS[o.tier || 'basic'], 
                      color: (o.tier || 'basic') === 'premium' ? '#000' : 'white', 
                      fontSize: '10px', 
                      padding: '2px 8px',
                      borderRadius: '10px',
                      fontWeight: '600'
                    }}
                  >
                    {TIER_LABELS[o.tier || 'basic']}
                  </span>
                  {!o.user_active && <span className="badge" style={{ background: 'var(--error)', color: 'white', fontSize: '10px', padding: '2px 6px' }}>{t('common.inactive')}</span>}
                </div>
                <p className="text-secondary text-xs">{o.email} • {o.property_count || 0} {t('admin.properties').toLowerCase()}</p>
                {o.line_id && <p className="text-secondary text-xs">LINE: {o.line_id}</p>}
              </div>
              <div className="flex items-center gap-2">
                <select
                  className="input"
                  value={o.tier || 'basic'}
                  onChange={(e) => handleTierChange(o.id, e.target.value)}
                  style={{ 
                    padding: '4px 8px', 
                    fontSize: '12px', 
                    width: '100px',
                    background: TIER_COLORS[o.tier || 'basic'] + '20',
                    borderColor: TIER_COLORS[o.tier || 'basic']
                  }}
                >
                  <option value="basic">Basic</option>
                  <option value="middle">Middle</option>
                  <option value="premium">Premium</option>
                </select>
                <button onClick={() => handleEdit(o)} className="btn btn-secondary" style={{ padding: '6px 10px', fontSize: '12px' }}>{t('common.edit')}</button>
              </div>
            </div>
          ))
        )}
    </div>
  );
}

export default AdminOwners;
